import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Play, Pause, RotateCcw } from 'lucide-react';
import { T, springs } from '../../lib/tokens';
import { Btn } from '../primitives/Btn';
import { supabase } from '../../lib/supabase';

export const FocusMode = ({ open, task, onClose, onUpdate }) => {
  const [seconds, setSeconds] = useState(25 * 60); // 25 min Pomodoro
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState('focus'); // 'focus' | 'break'

  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => {
      setSeconds(prev => {
        if (prev <= 0) {
          setRunning(false);
          if (phase === 'focus') {
            setPhase('break');
            return 5 * 60; // 5 min break
          } else {
            setPhase('focus');
            return 25 * 60;
          }
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [running, phase]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const handleDone = async () => {
    if (task) {
      await supabase.from('tasks').update({ done: true, done_at: new Date().toISOString() }).eq('id', task.id);
      onUpdate();
    }
    onClose();
  };

  const reset = () => {
    setSeconds(phase === 'focus' ? 25 * 60 : 5 * 60);
    setRunning(false);
  };

  const progress = phase === 'focus'
    ? ((25 * 60 - seconds) / (25 * 60)) * 100
    : ((5 * 60 - seconds) / (5 * 60)) * 100;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(250,250,248,0.97)',
            backdropFilter: 'blur(40px)',
            WebkitBackdropFilter: 'blur(40px)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
          }}
        >
          {/* Close button */}
          <button onClick={onClose} style={{
            position: 'absolute', top: 24, right: 24,
            width: 40, height: 40, borderRadius: 10,
            border: '1px solid rgba(0,0,0,0.08)', background: 'rgba(0,0,0,0.03)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <X size={18} color={T.textMuted} />
          </button>

          {/* Phase label */}
          <motion.div
            key={phase}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              fontSize: 12, fontWeight: 700, letterSpacing: '0.08em',
              textTransform: 'uppercase', marginBottom: 16,
              color: phase === 'focus' ? T.amber : T.green,
            }}
          >
            {phase === 'focus' ? 'Focus Time' : 'Break Time'}
          </motion.div>

          {/* Timer */}
          <div style={{ position: 'relative', marginBottom: 32 }}>
            <svg width="200" height="200" viewBox="0 0 200 200">
              <circle cx="100" cy="100" r="90" fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth="4" />
              <circle
                cx="100" cy="100" r="90" fill="none"
                stroke={phase === 'focus' ? T.amber : T.green}
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 90}`}
                strokeDashoffset={`${2 * Math.PI * 90 * (1 - progress / 100)}`}
                transform="rotate(-90 100 100)"
                style={{ transition: 'stroke-dashoffset 1s linear' }}
              />
            </svg>
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 48, fontWeight: 300, color: T.text,
              fontFamily: "'SF Mono', Menlo, Monaco, monospace",
              fontFeatureSettings: '"tnum"',
              letterSpacing: '-0.02em',
            }}>
              {formatTime(seconds)}
            </div>
          </div>

          {/* Task title */}
          {task && (
            <div style={{
              fontSize: 20, fontWeight: 600, color: T.text,
              marginBottom: 32, textAlign: 'center',
              maxWidth: 400, lineHeight: 1.3, letterSpacing: '-0.02em',
            }}>
              {task.title}
            </div>
          )}

          {/* Controls */}
          <div style={{ display: 'flex', gap: 12 }}>
            <Btn variant="ghost" onClick={reset}>
              <RotateCcw size={14} /> Reset
            </Btn>
            <Btn variant="primary" onClick={() => setRunning(!running)}>
              {running ? <><Pause size={14} /> Pause</> : <><Play size={14} /> {seconds === (phase === 'focus' ? 25 * 60 : 5 * 60) ? 'Start' : 'Resume'}</>}
            </Btn>
            <Btn variant="ghost" onClick={handleDone}>
              <Check size={14} /> Done
            </Btn>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
